// src/features/schedule/components/MobileDots.tsx
// 行動版 Timer Band 底部跳動小點動畫
import React, { useState, useEffect } from 'react';

const MOBILE_DOT_STYLES = `
  @keyframes dotBounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
  @keyframes dotShoot {
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(100px); opacity: 0; }
  }
  @keyframes dotEnter {
    0%   { transform: translateX(-20px) translateY(0);    opacity: 0; }
    40%  { transform: translateX(-4px)  translateY(-5px); opacity: 0.5; }
    70%  { transform: translateX(1px)   translateY(-2px); opacity: 0.5; }
    100% { transform: translateX(0)     translateY(0);    opacity: 0.5; }
  }
`;

// firing: 450ms，shifting: 400ms，entering: 400ms，idle: 瞬間重置
// 總序列 ~1250ms，每 5000ms 觸發一次
type DotPhase = 'idle' | 'firing' | 'shifting' | 'entering';

export interface MobileDotsProps {
  dotColor: string; // hex color
}

const MobileDots: React.FC<MobileDotsProps> = ({ dotColor }) => {
  const [phase, setPhase] = useState<DotPhase>('idle');
  // shifted 獨立控制三點的右移 transform，用 rAF 確保 transition 有起點
  const [shifted, setShifted] = useState(false);

  useEffect(() => {
    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;
    let t3: ReturnType<typeof setTimeout>;
    let raf: number;
    const interval = setInterval(() => {
      setPhase('firing');
      t1 = setTimeout(() => {
        setPhase('shifting');
        // 先讓瀏覽器渲染一幀 translateX(0)，再觸發 transition 到 translateX(16px)
        raf = requestAnimationFrame(() => {
          requestAnimationFrame(() => setShifted(true));
        });
      }, 450);
      t2 = setTimeout(() => setPhase('entering'), 850);
      t3 = setTimeout(() => {
        setPhase('idle');
        setShifted(false); // 瞬間重置（補入點已填充左側，無視覺跳動）
      }, 1250);
    }, 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      cancelAnimationFrame(raf);
    };
  }, []);

  const isFiring = phase === 'firing';
  const isShifting = phase === 'shifting';
  const isEntering = phase === 'entering';

  // 三個常規點的動畫樣式
  const dotStyle = (i: number): React.CSSProperties => {
    const bounceDelay = i * 120;
    const shiftDelay = i * 80; // 左→右逐一位移的視差

    if (isShifting || isEntering) {
      return {
        transform: shifted ? 'translateX(14px)' : 'translateX(0)',
        transition: `transform 0.35s ease ${shiftDelay}ms`,
      };
    }
    // idle / firing：正常彈跳
    return {
      animation: `dotBounce 1.2s ease-in-out ${bounceDelay}ms infinite`,
    };
  };

  return (
    <div className="relative flex items-end gap-2 h-5 overflow-visible">
      <style>{MOBILE_DOT_STYLES}</style>

      {/* 補入點：entering 時從左側彈入 */}
      {isEntering && (
        <span
          className="absolute left-0 w-1.5 h-1.5 rounded-full"
          style={{
            backgroundColor: dotColor,
            opacity: 0.5,
            animation: 'dotEnter 0.4s ease-out forwards',
          }}
        />
      )}

      {/* 點 0、1、2：持續彈跳，shifting 時逐一右移 */}
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{
            backgroundColor: dotColor,
            opacity: 0.5 + i * 0.15,
            ...dotStyle(i),
          }}
        />
      ))}

      {/* 點 3（最右側）：idle/firing 時彈跳，firing 時射出，shifting/entering 時隱藏 */}
      {!isShifting && !isEntering && (
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{
            backgroundColor: dotColor,
            opacity: isFiring ? undefined : 0.9,
            animation: isFiring
              ? 'dotShoot 0.45s cubic-bezier(0.4, 0, 1, 1) forwards'
              : `dotBounce 1.2s ease-in-out 360ms infinite`,
          }}
        />
      )}
    </div>
  );
};

export default MobileDots;
